import React from 'react';
import { Eye, MousePointer2, FileImage, FileText, Copy, Crown } from 'lucide-react';

const STATS = [
  { key: 'views',           label: 'Views',           Icon: Eye },
  { key: 'uses',            label: 'Uses',            Icon: MousePointer2 },
  { key: 'png_downloads',   label: 'PNG Downloads',   Icon: FileImage },
  { key: 'jpg_downloads',   label: 'JPG Downloads',   Icon: FileText },
  { key: 'copies_created',  label: 'Copies Created',  Icon: Copy },
  { key: 'pro_conversions', label: 'Pro Conversions', Icon: Crown, highlight: true },
];

/**
 * TemplateStatsCard — surfaces the six counter fields the backend
 * maintains on each template document. Pro Conversions is visually
 * highlighted because it's the primary monetization signal.
 *
 * The same counter shape supports future "Top Performing" and
 * "Trending" lookups via aggregation on the same fields.
 *
 * Props:
 *   template — the template doc (any object with the 6 counter fields)
 *   variant  — 'card' (default; full sidebar card) | 'inline' (compact row)
 */
export default function TemplateStatsCard({ template, variant = 'card' }) {
  if (!template) return null;
  const value = (key) => Number(template[key] || 0);

  if (variant === 'inline') {
    return (
      <div
        data-testid="template-stats-inline"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600"
      >
        {STATS.map(({ key, label, Icon, highlight }) => (
          <span
            key={key}
            className={`inline-flex items-center gap-1 ${highlight ? 'text-amber-700 font-semibold' : ''}`}
            title={label}
          >
            <Icon className="w-3 h-3" />
            <span className="text-gray-500">{label}:</span>
            <strong className="tabular-nums">{value(key).toLocaleString()}</strong>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      data-testid="template-stats-card"
      className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3"
    >
      <h2 className="text-sm font-semibold text-gray-700">Template Stats</h2>
      <p className="text-xs text-gray-500 -mt-2">All-time counters from analytics.</p>
      <div className="grid grid-cols-2 gap-3 pt-1">
        {STATS.map(({ key, label, Icon, highlight }) => (
          <div
            key={key}
            className={`p-3 rounded-lg border ${highlight ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}
          >
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
              <Icon className={`w-3.5 h-3.5 ${highlight ? 'text-amber-600' : ''}`} />
              <span>{label}</span>
            </div>
            <p className={`text-xl font-bold tabular-nums ${highlight ? 'text-amber-800' : 'text-gray-900'}`}>
              {value(key).toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
